import type { QueryPlan } from '@aibi/analysis-engine';
import {
  AI_ACTION_TYPES,
  type AIAction,
  type AIActionType,
  type AnalysisState,
  type ScalarValue,
} from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import type { ErrorCode } from '@aibi/shared';
import type { ModelChangeProposal } from '../apply/apply-ai-actions.js';
import {
  aiContextFingerprint,
  buildAIContext,
  serializeAIContext,
  type AIVisualizationCapability,
} from '../context/ai-context.js';
import {
  actionError,
  type AIActionError,
  type AIClarification,
  type AIDiagnostic,
} from '../errors.js';
import {
  explainAnalysis,
  type AnalysisExplanation,
  type QueryResultMetadata,
} from '../explain/explain-analysis.js';
import { guardPrompt } from '../guardrails/input-guards.js';
import {
  createInMemoryAIHistory,
  redactPrompt,
  type AIHistory,
  type AIHistoryStatus,
} from '../history/ai-history.js';
import {
  DEFAULT_AI_CONTEXT_LIMITS,
  DEFAULT_AI_CORE_LIMITS,
  type AIContextLimits,
  type AICoreLimits,
} from '../limits.js';
import { aiResponseJsonSchema } from '../protocol.js';
import type { AIProvider, AIProviderKind } from '../provider.js';
import { isAborted } from '../providers/adapters.js';
import { buildSystemInstructions } from '../providers/system-instructions.js';
import {
  suggestVisualizationForState,
  type VisualizationSuggestion,
} from '../suggest/suggest-visualization.js';
import { validateAIOutput } from '../validation/pipeline.js';
import { describeAction } from './describe-action.js';
import { analysisStateFingerprint } from './fingerprint.js';

export interface AICoreOptions {
  readonly provider: AIProvider;
  readonly limits?: AICoreLimits;
  readonly contextLimits?: AIContextLimits;
  readonly history?: AIHistory;
  readonly allowedActions?: readonly AIActionType[];
  readonly now?: () => Date;
}

export interface AICoreRequest {
  readonly prompt: string;
  readonly index: SemanticModelIndex;
  /** Estado base (tarjeta actual o un estado vacio para una tarjeta nueva). */
  readonly state: AnalysisState;
  readonly visualizations: readonly AIVisualizationCapability[];
  readonly members?: Readonly<Record<string, readonly ScalarValue[]>>;
  /** Plan y metadatos del resultado actuales, solo para explicaciones. */
  readonly plan?: QueryPlan | null;
  readonly result?: QueryResultMetadata | null;
}

interface Trace {
  readonly requestId: string;
  readonly providerId: string;
}

/** Propuesta validada: el estado candidato NO se ha aplicado a ningun documento. */
export interface AIProposal extends Trace {
  readonly kind: 'proposal';
  readonly actions: readonly AIAction[];
  readonly summary: readonly string[];
  readonly rationale?: string;
  /** Huella del estado base; permite detectar propuestas obsoletas antes de aplicarlas. */
  readonly baseStateFingerprint: string;
  readonly state: AnalysisState;
  readonly stateActionIds: readonly string[];
  readonly modelChanges: readonly ModelChangeProposal[];
  readonly plan: QueryPlan | null;
}

export type AICoreResponse =
  | AIProposal
  | (Trace & {
      readonly kind: 'explanation';
      readonly actionIds: readonly string[];
      readonly explanation: AnalysisExplanation;
    })
  | (Trace & {
      readonly kind: 'suggestion';
      readonly actionIds: readonly string[];
      readonly suggestion: VisualizationSuggestion;
    })
  | (Trace & { readonly kind: 'clarification'; readonly clarification: AIClarification })
  | (Trace & {
      readonly kind: 'error';
      readonly error: AIActionError;
      readonly diagnostics: readonly AIDiagnostic[];
    });

export interface AICore {
  readonly provider: {
    readonly id: string;
    readonly kind: AIProviderKind;
    readonly displayName: string;
  };
  readonly history: AIHistory;
  handle(request: AICoreRequest, signal?: AbortSignal): Promise<AICoreResponse>;
}

/**
 * Orquestador del AI Core. Flujo: guardrails de entrada -> contexto acotado -> proveedor
 * (solo texto) -> pipeline de validacion -> respuesta tipada. Nunca aplica cambios: las
 * propuestas se aplican fuera (comando de workspace) tras una accion explicita del usuario.
 *
 * @example
 * const core = createAICore({ provider: createLocalRulesProvider() });
 * const reply = await core.handle({ prompt: 'ventas por region', index, state, visualizations });
 * if (reply.kind === 'proposal') showProposal(reply.summary);
 */
export function createAICore(options: AICoreOptions): AICore {
  const limits = options.limits ?? DEFAULT_AI_CORE_LIMITS;
  const contextLimits = options.contextLimits ?? DEFAULT_AI_CONTEXT_LIMITS;
  const history = options.history ?? createInMemoryAIHistory();
  const allowedActions = options.allowedActions ?? AI_ACTION_TYPES;
  const now = options.now ?? (() => new Date());
  const { provider } = options;
  let sequence = 0;

  return {
    provider: { id: provider.id, kind: provider.kind, displayName: provider.displayName },
    history,
    async handle(request, signal) {
      sequence += 1;
      const requestId = `ai.req.${String(sequence)}`;
      const trace: Trace = { requestId, providerId: provider.id };
      let contextFingerprint: string | null = null;

      const record = (
        status: AIHistoryStatus,
        actions: readonly AIAction[] = [],
        diagnostics: readonly AIDiagnostic[] = [],
        unsafe = false,
      ): void => {
        history.record({
          requestId,
          timestamp: now().toISOString(),
          providerId: provider.id,
          prompt: redactPrompt(request.prompt, unsafe),
          status,
          actions,
          diagnosticCodes: [...new Set(diagnostics.map((d) => d.code))],
          contextFingerprint,
        });
      };
      const fail = (
        error: AIActionError,
        diagnostics: readonly AIDiagnostic[] = [],
        unsafe = false,
      ): AICoreResponse => {
        record(error.code === 'CANCELLED' ? 'cancelled' : 'rejected', [], diagnostics, unsafe);
        return { ...trace, kind: 'error', error, diagnostics };
      };
      const cancelled = (): AICoreResponse =>
        fail(actionError('CANCELLED', 'Solicitud cancelada.', { type: 'unknown' }));

      const prompt = guardPrompt(request.prompt, limits);
      if (!prompt.ok) {
        return fail(
          actionError(prompt.error.code, prompt.error.message, { type: 'unknown' }),
          [prompt.error],
          prompt.error.code === 'UNSAFE_CONTENT',
        );
      }

      const context = buildAIContext(
        {
          index: request.index,
          analysisState: request.state,
          visualizations: request.visualizations,
          ...(request.members === undefined ? {} : { members: request.members }),
        },
        contextLimits,
      );
      if (!context.ok) {
        return fail(actionError(context.error.code, context.error.message, { type: 'unknown' }), [
          context.error,
        ]);
      }
      contextFingerprint = aiContextFingerprint(context.value);
      if (isAborted(signal)) return cancelled();

      const generated = await provider.generate(
        {
          requestId,
          locale: context.value.locale,
          systemInstructions: buildSystemInstructions(context.value.locale, allowedActions),
          context: context.value,
          serializedContext: serializeAIContext(context.value),
          userMessage: prompt.value,
          allowedActions,
          responseSchema: aiResponseJsonSchema(),
        },
        signal,
      );
      if (isAborted(signal) || (!generated.ok && generated.error.code === 'CANCELLED')) {
        return cancelled();
      }
      if (!generated.ok) {
        return fail(
          actionError('PROVIDER_ERROR', providerFailureMessage(generated.error.code), {
            type: 'unknown',
          }),
          [
            {
              stage: 'input',
              code: 'PROVIDER_ERROR',
              path: 'provider',
              message: generated.error.code,
            },
          ],
        );
      }

      const validated = validateAIOutput({
        raw: generated.value.content,
        context: context.value,
        index: request.index,
        state: request.state,
        allowedActions,
        limits,
      });
      if (!validated.ok) {
        const [first] = validated.error;
        return fail(
          actionError(
            first?.code ?? 'INVALID_SCHEMA',
            'La respuesta del asistente no supero la validacion y no se aplico ningun cambio.',
            { type: 'unknown' },
          ),
          validated.error,
        );
      }

      const output = validated.value;
      if (output.kind === 'clarification') {
        record('clarification');
        return { ...trace, kind: 'clarification', clarification: output.clarification };
      }
      if (output.kind === 'error') {
        record(
          'rejected',
          [],
          [{ stage: 'action', code: output.error.code, path: '', message: output.error.message }],
        );
        return { ...trace, kind: 'error', error: output.error, diagnostics: [] };
      }

      const { applied, actions } = output;
      const actionIds = actions.map((action) => action.id);
      if (applied.stateActionIds.length > 0 || applied.modelChanges.length > 0) {
        record('proposed', actions);
        return {
          ...trace,
          kind: 'proposal',
          actions,
          summary: actions.map((action) => describeAction(action, request.index)),
          ...(output.rationale === undefined ? {} : { rationale: output.rationale }),
          baseStateFingerprint: analysisStateFingerprint(request.state),
          state: applied.state,
          stateActionIds: applied.stateActionIds,
          modelChanges: applied.modelChanges,
          plan: output.plan,
        };
      }

      const explanationAction = applied.informational.find(
        (action) => action.type === 'requestExplanation',
      );
      const suggestionAction = applied.informational.find(
        (action) => action.type === 'suggestVisualization',
      );
      if (suggestionAction && !explanationAction) {
        const suggestion = buildSuggestion(suggestionAction.payload.type, request);
        record('suggestion', actions);
        return { ...trace, kind: 'suggestion', actionIds, suggestion };
      }
      if (explanationAction || applied.informational.length > 0) {
        const topic = explanationAction?.payload.topic ?? 'result';
        const measureId = explanationAction?.payload.measureId;
        const explanation = explainAnalysis(topic, {
          state: request.state,
          index: request.index,
          plan: request.plan ?? null,
          result: request.result ?? null,
          ...(measureId === undefined ? {} : { measureId }),
        });
        record('explanation', actions);
        return { ...trace, kind: 'explanation', actionIds, explanation };
      }
      return fail(
        actionError('STATE_INVALID', 'La instruccion no produce cambios en el analisis.', {
          type: 'unknown',
        }),
      );
    },
  };
}

const PROVIDER_FAILURES: Readonly<Partial<Record<ErrorCode, string>>> = {
  TIMEOUT: 'El proveedor de IA no respondio a tiempo.',
  UNAVAILABLE: 'El proveedor de IA no esta disponible.',
  UPSTREAM_ERROR: 'El proveedor de IA devolvio una respuesta no valida.',
  PAYLOAD_TOO_LARGE: 'La solicitud o la respuesta de IA supera el tamano permitido.',
  VALIDATION_ERROR: 'El servidor rechazo la solicitud de IA.',
};

/** Mensaje generico por codigo: nunca expone detalles del transporte ni del proveedor. */
const providerFailureMessage = (code: ErrorCode): string =>
  PROVIDER_FAILURES[code] ?? 'El proveedor de IA no pudo responder.';

function buildSuggestion(
  type: AnalysisState['visualization']['type'],
  request: AICoreRequest,
): VisualizationSuggestion {
  const computed = suggestVisualizationForState(
    request.state,
    request.index,
    request.visualizations,
  );
  if (computed?.type === type) return computed;
  const capability = request.visualizations.find((viz) => viz.type === type);
  return {
    type,
    label: capability?.label ?? type,
    requiredDimensions: request.state.dimensions.map((d) => d.key),
    requiredMeasures: request.state.measures.map((m) => m.key),
    rationale: 'Visualizacion propuesta por el asistente.',
    confidence: 0.5,
  };
}
